import { Link } from "@tanstack/react-router";
import { useTranslations } from "use-intl";

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@lawline/ui/components/table";

import { formatDate, formatDateTime } from "@platform/fmt";

import type { WebhookEndpoint } from "../webhooks.types";
import { WebhookHealthBadge } from "./webhook-health-badge";

// At most 10 rows, so a plain table: no sorting, paging or column state to manage.
export function WebhookEndpointsTable({ endpoints }: { endpoints: WebhookEndpoint[] }) {
	const t = useTranslations("webhooks.table.columns");
	const tRoot = useTranslations("webhooks");

	return (
		<div className="overflow-x-auto rounded-md border contain-inline-size">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>{t("url")}</TableHead>
						<TableHead>{t("status")}</TableHead>
						<TableHead className="hidden md:table-cell">{t("events")}</TableHead>
						<TableHead className="hidden md:table-cell">{t("lastSuccess")}</TableHead>
						<TableHead className="hidden sm:table-cell">{t("created")}</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{endpoints.length === 0 ? (
						<TableRow>
							<TableCell colSpan={5} className="text-muted-foreground h-20 text-center text-sm">
								{tRoot("emptyState")}
							</TableCell>
						</TableRow>
					) : (
						endpoints.map((endpoint) => (
							<TableRow key={endpoint.id}>
								<TableCell className="max-w-64">
									<Link
										to="/webhooks/$endpointId"
										params={{ endpointId: endpoint.id }}
										className="block truncate font-mono text-xs font-medium hover:underline"
									>
										{endpoint.url}
									</Link>
									{endpoint.description && (
										<span className="text-muted-foreground block truncate text-xs">
											{endpoint.description}
										</span>
									)}
								</TableCell>
								<TableCell>
									<WebhookHealthBadge endpoint={endpoint} />
								</TableCell>
								<TableCell className="hidden font-mono text-xs md:table-cell">
									{endpoint.eventTypes.join(", ")}
								</TableCell>
								<TableCell className="hidden md:table-cell">
									{formatDateTime(endpoint.lastSuccessAt)}
								</TableCell>
								<TableCell className="hidden sm:table-cell">
									{formatDate(endpoint.createdAt)}
								</TableCell>
							</TableRow>
						))
					)}
				</TableBody>
			</Table>
		</div>
	);
}
